import type {
  GuestAccessState,
  ParticipantInvitationStatus,
  ParticipantInvitationSummary,
  RecordAccessEntry,
  RecordAccessLevel,
} from "@closer/shared";
import { GUEST_ACCESS_STATE_NAMES, RECORD_ACCESS_STATE_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

const DAVET_DURUMU: Record<ParticipantInvitationStatus, string> = {
  pending: "Davet bekliyor",
  accepted: "Kabul edildi",
  rejected: "Reddedildi",
  cancelled: "İptal edildi",
  expired: "Süresi doldu",
};

function gunSonu(gun: string): string {
  return new Date(`${gun}T23:59:59`).toISOString();
}
function tarih(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("tr-TR") : "";
}

/**
 * SÖZLEŞME ERİŞİMİ (CLOS-1127 · rapor S.2–S.7 · Halit K3/K6/K7).
 *
 * Modal yalnız İKİ iş yapar: (1) organizasyon içi mevcut üyeye doğrudan kayıt erişimi vermek
 * (invitation DEĞİL; CLOS-91 motoru), (2) hukuki taraf olmayan haricî gerçek kişiyi
 * yalnız GÖRÜNTÜLEME ile misafir olarak e-postayla davet etmek (tek Invitation Engine).
 * Müvekkil Alanı paylaşımı, hukuki taraf daveti ve iç sorumlu atama BURADA DEĞİL; kendi
 * yüzeylerinde. Liste kaynağa göre ayrılır: `creator`/`internal` iç listede, `guest` haricî
 * listede; `client_area` ve `party` satırları çizilmez (K3) — "Bilinmeyen üye" kavramı yok.
 *
 * ⚠ İKİ EKSEN: misafirde davet durumu (bekliyor/kabul edildi/…) ile erişim durumu
 * (aktif/süresi doldu/kaldırıldı) AYRI okunur; kaldırılan misafirin daveti "Kabul edildi"
 * kalır. ⚠ İKİ TARİH: davetin yanıt süresi ≠ erişim bitişi (K6) — formdaki "Bitiş tarihi"
 * kabul SONRASI erişimin biteceği gündür; boşsa kaldırılana kadar.
 */
export function ContractAccessDialog({
  contractId,
  open,
  onClose,
  arsivde = false,
}: {
  contractId: string;
  open: boolean;
  onClose: () => void;
  arsivde?: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [secili, setSecili] = useState("");
  const [seviye, setSeviye] = useState<RecordAccessLevel>("read");
  const [bitis, setBitis] = useState("");
  const [sureHedefi, setSureHedefi] = useState<string | null>(null);
  const [yeniBitis, setYeniBitis] = useState("");
  const [misafirEposta, setMisafirEposta] = useState("");
  const [misafirBitis, setMisafirBitis] = useState("");

  useEffect(() => {
    if (open) {
      setSecili("");
      setSeviye("read");
      setBitis("");
      setMisafirEposta("");
      setMisafirBitis("");
      setSureHedefi(null);
    }
  }, [open]);

  const erisimAnahtari = tenantKey(tenantId, "contract", contractId, "erisimler");
  const davetAnahtari = tenantKey(tenantId, "contract", contractId, "harici-davetler");
  const tazele = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: erisimAnahtari }),
      queryClient.invalidateQueries({ queryKey: davetAnahtari }),
    ]);

  const erisimler = useQuery({
    enabled: open,
    queryKey: erisimAnahtari,
    queryFn: async () =>
      (await api<RecordAccessEntry[]>(`/api/records/contract/${contractId}/access`, { tenantId }))
        .data,
  });
  const adaylar = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-adaylari"),
    queryFn: async () =>
      (
        await api<{ membershipId: string; name: string }[]>(
          `/api/records/contract/${contractId}/access/subjects`,
          { tenantId },
        )
      ).data,
  });
  const davetler = useQuery({
    enabled: open,
    queryKey: davetAnahtari,
    queryFn: async () =>
      (
        await api<ParticipantInvitationSummary[]>(
          `/api/contracts/${contractId}/participant-invitations`,
          { tenantId },
        )
      ).data,
  });

  const hataMetni = (hata: unknown, varsayilan: string) =>
    hata instanceof ApiRequestError ? hata.message : varsayilan;

  const erisimVer = useMutation({
    mutationFn: async () =>
      api(`/api/records/contract/${contractId}/access`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          subjectType: "membership",
          subjectId: secili,
          level: seviye,
          endsAt: bitis ? gunSonu(bitis) : undefined,
        }),
      }),
    onSuccess: async () => {
      await tazele();
      setSecili("");
      setBitis("");
      toast.success("Erişim verildi");
    },
    onError: (hata) => toast.error(hataMetni(hata, "Erişim verilemedi")),
  });
  const sureYenile = useMutation({
    mutationFn: async ({ accessId, endsAt }: { accessId: string; endsAt: string | null }) =>
      api(`/api/records/contract/${contractId}/access/${accessId}`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ endsAt }),
      }),
    onSuccess: async () => {
      await tazele();
      setSureHedefi(null);
      setYeniBitis("");
      toast.success("Erişim süresi güncellendi");
    },
    onError: (hata) => toast.error(hataMetni(hata, "Süre güncellenemedi")),
  });
  const kaldir = useMutation({
    mutationFn: async (accessId: string) =>
      api(`/api/records/contract/${contractId}/access/${accessId}`, {
        method: "DELETE",
        tenantId,
      }),
    onSuccess: async () => {
      await tazele();
      toast.success("Erişim kaldırıldı");
    },
    onError: (hata) => toast.error(hataMetni(hata, "Erişim kaldırılamadı")),
  });
  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const davetGonder = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/participant-invitations`, {
        method: "POST",
        tenantId,
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify({
          email: misafirEposta,
          accessLevel: "read",
          ...(misafirBitis ? { accessEndsAt: gunSonu(misafirBitis) } : {}),
        }),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await tazele();
      setMisafirEposta("");
      setMisafirBitis("");
      toast.success("Davet gönderildi");
    },
    onError: (hata) => toast.error(hataMetni(hata, "Davet gönderilemedi")),
  });
  const davetIptal = useMutation({
    mutationFn: async (inviteId: string) =>
      api(`/api/contracts/${contractId}/participant-invitations/${inviteId}/cancel`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await tazele();
      toast.success("Davet iptal edildi");
    },
    onError: (hata) => toast.error(hataMetni(hata, "Davet iptal edilemedi")),
  });

  /** İç liste: oluşturan + organizasyon üyesi grant'leri; kaldırılmışlar geçmişte kalır. */
  const icSatirlar = (erisimler.data ?? []).filter(
    (e) => (e.kind === "creator" || e.kind === "internal") && e.accessState !== "revoked",
  );
  /** Haricî liste: iptal/ret dışındaki davetler; erişim ekseni ayrı sütunda. */
  const misafirler = (davetler.data ?? []).filter(
    (d) => d.status !== "cancelled" && d.status !== "rejected",
  );
  const seviyeAdi: Record<RecordAccessLevel, string> = { read: "Görüntüleme", write: "Düzenleme" };
  const misafirErisimTonu: Record<GuestAccessState, "success" | "warning" | "neutral"> = {
    active: "success",
    expired: "warning",
    revoked: "neutral",
    none: "neutral",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sözleşme Erişimi"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Kapat
        </Button>
      }
    >
      <div className="space-y-5">
        <section aria-labelledby="ic-erisim-baslik" className="space-y-3">
          <h3 id="ic-erisim-baslik" className="text-sm font-semibold text-fg-heading">
            Organizasyon içi erişim
          </h3>
          <p className="text-xs text-fg-soft">
            Organizasyonunuzun mevcut üyesine bu sözleşme için doğrudan erişim verin. Bu bir davet
            değildir; erişim hemen başlar.
          </p>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (secili) erisimVer.mutate();
            }}
          >
            {adaylar.isPending ? (
              <div role="status" aria-label="Üyeler yükleniyor">
                <Skeleton lines={2} />
              </div>
            ) : (
              <>
                <FormField label="Üye" required>
                  <Select
                    aria-label="Üye"
                    value={secili}
                    onChange={(e) => setSecili(e.target.value)}
                  >
                    <option value="">Seçin</option>
                    {(adaylar.data ?? []).map((aday) => (
                      <option key={aday.membershipId} value={aday.membershipId}>
                        {aday.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Erişim seviyesi" required>
                  <Select
                    aria-label="Erişim seviyesi"
                    value={seviye}
                    onChange={(e) => setSeviye(e.target.value as RecordAccessLevel)}
                  >
                    <option value="read">Görüntüleme</option>
                    <option value="write">Düzenleme</option>
                  </Select>
                </FormField>
                <FormField label="Bitiş tarihi (boşsa süresiz)">
                  <Input
                    aria-label="Bitiş tarihi"
                    type="date"
                    value={bitis}
                    onChange={(e) => setBitis(e.target.value)}
                  />
                </FormField>
                <Button type="submit" loading={erisimVer.isPending} disabled={secili === ""}>
                  Erişim ver
                </Button>
              </>
            )}
          </form>
          <div>
            <h4 className="mb-2 text-sm font-medium">Organizasyon içi erişimi olanlar</h4>
            {erisimler.isPending ? (
              <div role="status" aria-label="Erişimler yükleniyor">
                <Skeleton lines={3} />
              </div>
            ) : icSatirlar.length === 0 ? (
              <EmptyState
                title="Henüz erişim verilmedi"
                description="Bu sözleşmeyi yalnız oluşturan ve erişim verilenler görebilir."
              />
            ) : (
              <ul className="divide-y divide-border" data-testid="ic-erisim-listesi">
                {icSatirlar.map((erisim) => (
                  <li
                    key={erisim.id}
                    className="flex items-center justify-between gap-2 py-2"
                    data-testid={`ic-erisim-${erisim.id}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">{erisim.subjectName}</p>
                      <p className="text-xs text-fg-soft">
                        {seviyeAdi[erisim.level]}
                        {erisim.kind === "creator" ? " · Oluşturan" : ""}
                        {erisim.accessState === "expired"
                          ? ` · ${RECORD_ACCESS_STATE_NAMES.expired}`
                          : erisim.endsAt
                            ? ` · ${tarih(erisim.endsAt)} tarihine kadar`
                            : ""}
                      </p>
                    </div>
                    {erisim.kind === "creator" ? null : (
                      <span className="flex flex-wrap items-end gap-1">
                        {sureHedefi === erisim.id ? (
                          <>
                            <FormField label="Yeni bitiş">
                              <Input
                                aria-label="Yeni bitiş"
                                type="date"
                                value={yeniBitis}
                                onChange={(olay) => setYeniBitis(olay.target.value)}
                              />
                            </FormField>
                            <Button
                              type="button"
                              size="sm"
                              loading={sureYenile.isPending}
                              onClick={() =>
                                sureYenile.mutate({
                                  accessId: erisim.id,
                                  endsAt: yeniBitis ? gunSonu(yeniBitis) : null,
                                })
                              }
                            >
                              Kaydet
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setSureHedefi(null)}
                            >
                              Vazgeç
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSureHedefi(erisim.id);
                              setYeniBitis(erisim.endsAt ? erisim.endsAt.slice(0, 10) : "");
                            }}
                          >
                            Süreyi düzenle
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          loading={kaldir.isPending}
                          onClick={() => kaldir.mutate(erisim.id)}
                        >
                          Kaldır
                        </Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section aria-labelledby="misafir-baslik" className="space-y-3">
          <h3 id="misafir-baslik" className="text-sm font-semibold text-fg-heading">
            Haricî misafirler
          </h3>
          <p className="text-xs text-fg-soft" data-testid="misafir-yardim">
            Sözleşmenin hukuki tarafı olmayan bir kişiye görüntüleme erişimi verin. Hukuki taraflar
            “Sözleşmenin Tarafları” bölümünden davet edilir.
          </p>
          <p data-testid="haricî-kapsam" className="text-xs text-fg-soft">
            Misafir yalnız sözleşmenin kendisini görür; büronuzun iç notları, görevleri, iç onayları
            ve AI analizleri paylaşılmaz. Düzenleme, yorum, revizyon ve indirme yoktur.
          </p>
          {arsivde ? (
            <p className="text-xs text-fg-soft">
              Sözleşme arşivde: yeni katılımcı davet edilemez. Gönderilmiş davetler ve mevcut
              erişimler burada kalmaya devam ediyor.
            </p>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (misafirEposta) davetGonder.mutate();
              }}
            >
              <FormField label="E-posta" required>
                <Input
                  aria-label="Haricî e-posta"
                  type="email"
                  value={misafirEposta}
                  onChange={(e) => setMisafirEposta(e.target.value)}
                />
              </FormField>
              <p className="text-sm text-fg" data-testid="misafir-seviye">
                Erişim: <strong>Görüntüleme</strong>
              </p>
              <FormField
                label="Bitiş tarihi (isteğe bağlı)"
                hint="Kabulden sonra erişimin biteceği gün; boşsa siz kaldırana kadar sürer. Davetin yanıt süresi ayrıdır."
              >
                <Input
                  aria-label="Misafir bitiş tarihi"
                  type="date"
                  value={misafirBitis}
                  onChange={(e) => setMisafirBitis(e.target.value)}
                />
              </FormField>
              <Button type="submit" loading={davetGonder.isPending} disabled={misafirEposta === ""}>
                Davet gönder
              </Button>
            </form>
          )}
          {misafirler.length > 0 && (
            <ul className="divide-y divide-border" data-testid="misafir-listesi">
              {misafirler.map((davet) => (
                <li
                  key={davet.id}
                  className="flex items-center justify-between gap-2 py-2"
                  data-testid={`misafir-${davet.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm">{davet.email}</p>
                    <p className="flex flex-wrap items-center gap-1 text-xs text-fg-soft">
                      <span>Görüntüleme</span>
                      <span>· Davet: {DAVET_DURUMU[davet.status]}</span>
                      {davet.status === "pending" ? (
                        <span>({tarih(davet.expiresAt)} tarihine kadar yanıtlanabilir)</span>
                      ) : null}
                      <span>· Erişim:</span>
                      <Badge tone={misafirErisimTonu[davet.accessState]}>
                        {GUEST_ACCESS_STATE_NAMES[davet.accessState]}
                      </Badge>
                      {davet.accessEndsAt && davet.accessState !== "revoked" ? (
                        <span>({tarih(davet.accessEndsAt)} tarihine kadar)</span>
                      ) : null}
                    </p>
                  </div>
                  {davet.status === "pending" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      loading={davetIptal.isPending}
                      onClick={() => davetIptal.mutate(davet.id)}
                    >
                      Daveti iptal et
                    </Button>
                  ) : davet.accessId && davet.accessState !== "revoked" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      loading={kaldir.isPending}
                      onClick={() => kaldir.mutate(davet.accessId!)}
                    >
                      Erişimi kaldır
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
